import _objectSpread from "@babel/runtime/helpers/objectSpread";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { usePreferences } from "@temp/@next/hooks";
import { isEqual } from "apollo-utilities";
import { LanguageCodeEnum } from "gqlTypes/globalTypes";
import React, { useMemo } from "react";
import { useAuth, useSaleorClient } from "./helpers";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
var useQuery = function useQuery(query) {
  var variables = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var _usePreferences = usePreferences(),
      locale = _usePreferences.preferences.locale;

  var variablesWithLocale = useMemo(function () {
    /* tslint:disable:prefer-object-spread */

    /* eslint-disable-next-line prefer-object-spread */
    return Object.assign({}, variables, {
      languageCode: LanguageCodeEnum[locale]
    });
  }, [locale, variables]);
  /* tslint:enable:prefer-object-spread */

  var saleor = useSaleorClient();
  var didMountRef = React.useRef(false);
  var prevDataRef = React.useRef(null);
  var prevUnsubRef = React.useRef(null);

  var _useAuth = useAuth(),
      authenticated = _useAuth.authenticated;

  var _React$useState = React.useState({
    data: null,
    error: null,
    loading: true
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      result = _React$useState2[0],
      setResult = _React$useState2[1];

  var setData = React.useCallback(function (data) {
    if (!isEqual(data, prevDataRef.current)) {
      prevDataRef.current = data;
      setResult({
        data: data,
        loading: false,
        error: null
      });
    } else {
      setResult(function (prevResult) {
        return _objectSpread({}, prevResult, {
          loading: false
        });
      });
    }
  }, []);

  var _React$useMemo = React.useMemo(function () {
    return saleor.legacyAPIProxy[query](variablesWithLocale, _objectSpread({}, options, {
      onError: function onError(error) {
        return setResult(function (prevResult) {
          return _objectSpread({}, prevResult, {
            loading: false,
            error: error
          });
        });
      },
      onUpdate: function onUpdate(data) {
        setData(data);
      }
    }));
  }, [query, options.skip, authenticated]),
      unsubscribe = _React$useMemo.unsubscribe,
      setOptions = _React$useMemo.setOptions,
      _refetch = _React$useMemo.refetch,
      _loadMore = _React$useMemo.loadMore;

  var refetch = React.useCallback(function (vars) {
    setResult(function (prevResult) {
      return _objectSpread({}, prevResult, {
        error: null,
        loading: true
      });
    });
    /* tslint:disable:prefer-object-spread */

    /* eslint-disable-next-line prefer-object-spread */

    var mergedVars = Object.assign({}, variablesWithLocale, vars);
    /* tslint:enable:prefer-object-spread */

    _refetch(mergedVars);
  }, [variablesWithLocale, _refetch]);
  var loadMore = React.useCallback(function (vars) {
    var mergeResults = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

    if (_loadMore) {
      setResult(function (prevResult) {
        return _objectSpread({}, prevResult, {
          error: null,
          loading: true
        });
      });
      /* tslint:disable:prefer-object-spread */

      /* eslint-disable-next-line prefer-object-spread */

      var mergedVars = Object.assign({}, variablesWithLocale, vars);
      /* tslint:enable:prefer-object-spread */

      _loadMore(mergedVars, mergeResults);
    }
  }, [_loadMore, variablesWithLocale]);
  React.useEffect(function () {
    // skip on initial mount
    if (didMountRef.current) {
      refetch(variablesWithLocale);
    } else {
      didMountRef.current = true;
    }
  }, [JSON.stringify(variablesWithLocale)]); // unsubscribe from watcher on dismount

  React.useEffect(function () {
    if (prevUnsubRef.current) {
      prevUnsubRef.current();
    }

    prevUnsubRef.current = unsubscribe;
    return function () {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [options.skip, authenticated]);
  return _objectSpread({}, result, {
    loadMore: loadMore,
    refetch: refetch,
    setOptions: setOptions
  });
};

export var queryWithVariablesFactory = function queryWithVariablesFactory(query) {
  return function (variables, options) {
    return useQuery(query, variables, options);
  };
};
export var queryFactory = function queryFactory(query) {
  return function (options) {
    return useQuery(query, undefined, options);
  };
};